import {
  Article,
  ContentfulArticle,
  ArticleScope,
  User,
  ArticleCommon,
} from '../types'
import {
  filterPublished,
  getArticle,
  getArticles,
  getArticlesByKeyword,
  getPreviewArticle,
} from './api/contentful'
import { printError } from './log'
import { withProtocol } from './url'
import { isTrial, isEnterprise, isLatestPlan } from './user'

export const fetchArticles = async (cursor: number = 0, limit?: number) => {
  let articles: Article[] = []
  try {
    const data = await getArticles(cursor, limit)
    if (data) {
      articles = data.items.map((res) =>
        convertArticle({ ...res.fields, id: res.sys.id })
      )
    }
  } catch (e) {
    printError(e)
  }
  return articles
}

export const fetchArticlesByKeyword = async (keyword: string) => {
  let articles: Article[] = []
  try {
    const data = await getArticlesByKeyword(keyword)
    if (data) {
      articles = data.items.map((res) =>
        convertArticle({ ...res.fields, id: res.sys.id })
      )
    }
  } catch (e) {
    printError(e)
  }
  return articles
}

export const fetchArticlesTotalCount = async () => {
  let total = 0
  try {
    const data = await getArticles()
    if (data) {
      total = data.total
    }
  } catch (e) {
    printError(e)
  }
  return total
}

export const fetchArticle = async (slugOrId: string) => {
  let article
  try {
    const contetnfulArticle = await getArticle(slugOrId)
    if (contetnfulArticle) {
      article = convertArticle(contetnfulArticle)
    }
  } catch (e) {
    printError(e)
  }
  return article
}

export const fetchPreviewArticle = async (id: string) => {
  let article
  try {
    const contetnfulArticle = await getPreviewArticle(id)
    if (contetnfulArticle) {
      article = convertArticle(contetnfulArticle)
    }
  } catch (e) {
    printError(e)
  }
  return article
}

export const convertArticle = (
  article: ContentfulArticle,
  isRelatedArticle?: Boolean
): Article => {
  const reportFile = article.reportFile
    ? {
        url: withProtocol(article.reportFile.fields.file.url),
        title: article.reportFile.fields.title,
      }
    : undefined
  const headerImage = {
    url: withProtocol(article.headerImage.fields.file.url),
    title: article.headerImage.fields.title,
  }

  const pickupImage = article.pickupImage && {
    url: withProtocol(article.pickupImage.fields.file.url),
    title: article.pickupImage.fields.title,
  }

  // NOTE: 相互に関連記事になっている場合に無限ループとなるため
  const relatedArticles = isRelatedArticle
    ? []
    : article.relatedArticles &&
      filterPublished(article.relatedArticles).map((a) =>
        convertArticle({ ...a.fields, id: a.sys.id }, true)
      )

  const newTags =
    article.newTags &&
    filterPublished(article.newTags).map((tag) => ({
      name: tag.fields.name,
      sfdcId: tag.fields.sfdcId,
      slug: tag.fields.slug,
    }))

  const companies =
    article.companies &&
    filterPublished(article.companies).map((company) => ({
      shortname: company.fields.shortname,
      name: company.fields.name,
    }))

  const investors =
    article.investors &&
    filterPublished(article.investors).map((investor) => ({
      shortname: investor.fields.shortname,
      name: investor.fields.name,
    }))

  return {
    ...article,
    headerImage,
    pickupImage,
    relatedArticles: relatedArticles,
    newTags,
    companies,
    investors,
    reportFile,
    scope: article.scope as ArticleScope,
  }
}

// NOTE: Actual impl. has hard-coded role (`enterprise`).
const limitMarker = /<!--\s*?limit: enterprise\s*?-->[^]+/

export const hasLimitMarker = (text: string) => {
  return limitMarker.test(text)
}

export const contentBodyOf = (
  text: string,
  options: { permitted: boolean }
) => {
  return options.permitted
    ? text
    : hasLimitMarker(text)
      ? text.replace(limitMarker, '')
      : ''
}

export const permittedToRead = (
  user: User,
  article: Article | ArticleCommon
) => {
  switch (article.scope) {
    case 'all':
      return true
    case 'enterprise':
      return isTrial(user) || (isEnterprise(user) && isLatestPlan(user))
  }

  // Fallback: Enterprise users can read all anyway.
  console.warn(`Unexpected scope: ${article.scope}`)
  return isEnterprise(user) && isLatestPlan(user)
}

export type LockType = 'none' | 'locked' | 'unlocked'

export const lockTypeOf = (
  user: User,
  article: Article | ArticleCommon
): LockType => {
  if (article.scope === 'all') {
    return 'none'
  }
  return permittedToRead(user, article) ? 'unlocked' : 'locked'
}

export const isArticle = (content: any): content is Article | ArticleCommon =>
  content.scope !== undefined
